/**
 * Custom hook for managing header data
 * Provides cached access to header content from Strapi
 */

import { useEffect, useState } from 'react';

import { fetchHeaderData, HeaderData, NavigationItem } from '@/services';
import { getImageUrl } from '@/services/utils';

interface UseHeaderDataReturn {
  headerData: HeaderData | null;
  loading: boolean;
  error: string | null;
  // Transformed header data
  logo?: string;
  navigationItems: NavigationItem[];
  isActive: boolean;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and manage header data from Strapi
 * @returns Object with header data, loading state, error state, and refetch function
 */
export function useHeaderData(): UseHeaderDataReturn {
  const [headerData, setHeaderData] = useState<HeaderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Function to fetch header data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchHeaderData();
      setHeaderData(data);
    } catch (err) {
      console.error('Error fetching header data:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to fetch header data'
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      fetchData();
    } else {
      // In SSR, just set loading to false
      setLoading(false);
    }
  }, []);

  // Transform logo URL
  const logo = headerData?.logo?.url ? getImageUrl(headerData.logo) : undefined;

  return {
    headerData,
    loading,
    error,
    logo,
    navigationItems: headerData?.navigationItems ?? [],
    isActive: headerData?.isActive ?? false,
    refetch: fetchData,
  };
}
